import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MemoryIcon from '@mui/icons-material/Memory';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import { Container, SearchBar, Table, TableTitle } from '../components';
import Pagination from '../components/Pagination/Pagination';
import usePagination from '../components/Pagination/usePagination';
import FirmwareOverview from '../features/metronDevices/FirmwareOverview';
import { getMetronDeviceColumns } from '../features/metronDevices/columns';
import { FIRMWARE_STATUS_OPTIONS, LIMIT } from '../features/metronDevices/constants';
import { useMetronDevicesStore } from '../features/metronDevices/state/use-metron-devices-store';

const MetronDevices = () => {
  const { items, total, firmware, summary, isLoading, loaded, error, fetchDevices } = useMetronDevicesStore();
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT });
  const totalPages = Math.ceil(total / limit);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [firmwareStatus, setFirmwareStatus] = useState('');

  const handleSort = useCallback(
    (newSortBy, newSortOrder) => {
      setSortBy(newSortBy || '');
      setSortOrder(newSortOrder || '');
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  const handleSearch = useCallback(
    (value) => {
      setSerialNumber(value || '');
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  const handleStatus = useCallback(
    (value) => {
      setFirmwareStatus(value || '');
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  useEffect(() => {
    fetchDevices({ page, count: limit, serialNumber, firmwareStatus, sortBy, sortOrder });
  }, [fetchDevices, firmwareStatus, limit, page, serialNumber, sortBy, sortOrder]);

  const columns = useMemo(() => getMetronDeviceColumns(), []);

  const inputs = useMemo(
    () => [
      {
        type: 'simpleSearch',
        placeholder: 'Поиск по заводскому номеру...',
        defaultValue: serialNumber,
        handleSearch,
        prefix: <MemoryIcon fontSize="small" className="text-gray-500" />,
      },
      {
        key: `firmware-status-${firmwareStatus}`,
        type: 'selector',
        placeholder: 'Статус прошивки',
        options: FIRMWARE_STATUS_OPTIONS,
        defaultValue: firmwareStatus,
        onChange: handleStatus,
        prefix: <NewReleasesOutlinedIcon fontSize="small" className="text-gray-500 mr-2" />,
      },
    ],
    [firmwareStatus, handleSearch, handleStatus, serialNumber]
  );

  return (
    <Container>
      <div className="max-w-7xl mx-auto">
        <TableTitle title="Метрон" />
        <SearchBar inputs={inputs} />
      </div>

      <FirmwareOverview summary={summary} firmware={loaded ? firmware : undefined} firmwareStatus={firmwareStatus} onStatusChange={handleStatus} />

      <Table
        columns={columns}
        data={items}
        loading={isLoading}
        error={error}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        rowKey="counter_id"
        getRowClassName={(row) => (row?.firmware_status === 'outdated' ? 'bg-orange-50' : null)}>
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} initialLimit={LIMIT} />}
      </Table>
    </Container>
  );
};

export default MetronDevices;
